import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Card, Button, Input, TextArea, Select, Modal } from '@components/common/UIComponents';
import { 
  addJournalEntry, 
  getJournalInRange,
  updateJournalEntry,
  deleteJournalEntry 
} from '@services/firestoreService';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  startOfDay,
  endOfDay,
  subMonths,
  addMonths,
  subWeeks,
  addWeeks,
  subDays,
  addDays,
  eachDayOfInterval
} from 'date-fns';
import { 
  BookOpen, 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Search,
  Edit2,
  Trash2,
  Smile,
  Meh,
  Frown,
  Angry,
  Heart
} from 'lucide-react';

const MOODS = [
  { value: 5, label: 'Excellent', icon: Heart, color: 'text-green-600', bg: 'bg-green-100', emoji: '😄' },
  { value: 4, label: 'Good', icon: Smile, color: 'text-blue-600', bg: 'bg-blue-100', emoji: '😊' },
  { value: 3, label: 'Neutral', icon: Meh, color: 'text-yellow-600', bg: 'bg-yellow-100', emoji: '😐' },
  { value: 2, label: 'Bad', icon: Frown, color: 'text-orange-600', bg: 'bg-orange-100', emoji: '😔' },
  { value: 1, label: 'Terrible', icon: Angry, color: 'text-red-600', bg: 'bg-red-100', emoji: '😢' }
];

const Journal = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [period, setPeriod] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    mood: 3,
    date: format(new Date(), "yyyy-MM-dd")
  });

  useEffect(() => {
    if (user) {
      loadJournalData();
    }
  }, [user, period, currentDate]);

  const getPeriodRange = () => {
    if (period === 'day') {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate)
      };
    } else if (period === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 })
      };
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
    }
  };

  const loadJournalData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { start, end } = getPeriodRange();
      const journalEntries = await getJournalInRange(user.uid, start, end);
      setEntries(journalEntries);
    } catch (error) {
      console.error('Error loading journal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (direction) => {
    if (period === 'day') {
      setCurrentDate(direction === 'prev' ? subDays(currentDate, 1) : addDays(currentDate, 1));
    } else if (period === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
    }
  };

  const handleAddEntry = async () => {
    if (!formData.title.trim() || !formData.content.trim()) return;

    try {
      await addJournalEntry(user.uid, {
        title: formData.title,
        content: formData.content,
        mood: parseInt(formData.mood),
        entryDate: formData.date
      });

      setShowAddModal(false);
      setFormData({
        title: '',
        content: '',
        mood: 3,
        date: format(new Date(), "yyyy-MM-dd")
      });
      loadJournalData();
    } catch (error) {
      console.error('Error adding journal entry:', error);
    }
  };

  const handleUpdateEntry = async () => {
    if (!selectedEntry || !formData.title.trim() || !formData.content.trim()) return;

    try {
      await updateJournalEntry(user.uid, selectedEntry.id, {
        title: formData.title,
        content: formData.content,
        mood: parseInt(formData.mood),
        entryDate: formData.date
      });

      setShowAddModal(false);
      setSelectedEntry(null);
      setFormData({
        title: '',
        content: '',
        mood: 3,
        date: format(new Date(), "yyyy-MM-dd")
      });
      loadJournalData();
    } catch (error) {
      console.error('Error updating journal entry:', error);
    }
  };

  const handleDelete = async (entryId) => {
    if (!confirm('Are you sure you want to delete this journal entry?')) return;

    try {
      await deleteJournalEntry(user.uid, entryId);
      setShowViewModal(false);
      setSelectedEntry(null);
      loadJournalData();
    } catch (error) {
      console.error('Error deleting journal entry:', error);
    }
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    setFormData({
      title: entry.title,
      content: entry.content,
      mood: entry.mood,
      date: entry.entryDate
    });
    setShowViewModal(false);
    setShowAddModal(true);
  };

  const handleViewEntry = (entry) => {
    setSelectedEntry(entry);
    setShowViewModal(true);
  };

  const filteredEntries = useMemo(() => {
    if (!searchTerm.trim()) return entries;
    
    const term = searchTerm.toLowerCase();
    return entries.filter(entry => 
      entry.title.toLowerCase().includes(term) || 
      entry.content.toLowerCase().includes(term)
    );
  }, [entries, searchTerm]);

  const getPeriodLabel = () => {
    if (period === 'day') {
      return format(currentDate, 'MMMM d, yyyy');
    } else if (period === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  const moodStats = useMemo(() => {
    if (entries.length === 0) return null;

    const moodCounts = entries.reduce((acc, entry) => {
      acc[entry.mood] = (acc[entry.mood] || 0) + 1;
      return acc;
    }, {});

    const avgMood = entries.reduce((sum, entry) => sum + entry.mood, 0) / entries.length;
    const mostCommonMood = Object.entries(moodCounts).reduce((a, b) => 
      moodCounts[a[0]] > moodCounts[b[0]] ? a : b
    )[0];

    return { avgMood, mostCommonMood: parseInt(mostCommonMood), moodCounts };
  }, [entries]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <BookOpen size={32} className="text-primary-600" />
            Daily Journal
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Reflect on your day and track your thoughts
          </p>
        </div>
        <Button onClick={() => {
          setSelectedEntry(null);
          setFormData({
            title: '',
            content: '',
            mood: 3,
            date: format(new Date(), "yyyy-MM-dd")
          });
          setShowAddModal(true);
        }}>
          <Plus size={20} className="mr-2" />
          New Entry
        </Button>
      </div>

      {/* Period Selector */}
      <Card>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleNavigate('prev')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center gap-2 min-w-[200px] justify-center">
              <Calendar size={20} className="text-primary-600" />
              <span className="font-semibold text-gray-900 dark:text-white">
                {getPeriodLabel()}
              </span>
            </div>
            <button
              onClick={() => handleNavigate('next')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="flex gap-2">
            {['day', 'week', 'month'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  period === p
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Mood Statistics */}
      {moodStats && entries.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Mood Overview
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Entries</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{entries.length}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Average Mood</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{MOODS.find(m => m.value === Math.round(moodStats.avgMood))?.emoji}</span>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {moodStats.avgMood.toFixed(1)}
                </p>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Most Common</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{MOODS.find(m => m.value === moodStats.mostCommonMood)?.emoji}</span>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {MOODS.find(m => m.value === moodStats.mostCommonMood)?.label}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Search */}
      <Card>
        <div className="relative">
          <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search entries by title or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
      </Card>

      {/* Journal Entries */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Entries ({filteredEntries.length})
        </h3>

        {filteredEntries.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <BookOpen size={48} className="mx-auto mb-3 opacity-30" />
            <p>{searchTerm ? 'No entries found' : 'No entries yet'}</p>
            <p className="text-sm mt-1">
              {searchTerm ? 'Try a different search term' : 'Start writing about your day'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEntries.map((entry) => {
              const moodInfo = MOODS.find(m => m.value === entry.mood);
              return (
                <div
                  key={entry.id}
                  onClick={() => handleViewEntry(entry)}
                  className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{moodInfo?.emoji}</span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {entry.title}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {format(new Date(entry.entryDate), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                    {entry.content}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedEntry(null);
        }}
        title={selectedEntry ? 'Edit Entry' : 'New Journal Entry'}
      >
        <div className="space-y-4">
          <Input
            type="date"
            label="Date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            fullWidth
          />
          <Input
            type="text"
            label="Title"
            placeholder="What's on your mind?"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            fullWidth
          />
          <Select
            label="How was your day?"
            value={formData.mood}
            onChange={(e) => setFormData({ ...formData, mood: e.target.value })}
            options={MOODS}
            fullWidth
          />
          <TextArea
            label="Write about your day"
            placeholder="Share your thoughts, experiences, and reflections..."
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            rows={8}
            fullWidth
          />
          <div className="flex gap-3">
            <Button 
              onClick={selectedEntry ? handleUpdateEntry : handleAddEntry} 
              fullWidth
              disabled={!formData.title.trim() || !formData.content.trim()}
            >
              {selectedEntry ? 'Update Entry' : 'Save Entry'}
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowAddModal(false);
                setSelectedEntry(null);
              }} 
              fullWidth
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      {selectedEntry && (
        <Modal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedEntry(null);
          }}
          title={selectedEntry.title}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-3xl">{MOODS.find(m => m.value === selectedEntry.mood)?.emoji}</span>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {format(new Date(selectedEntry.entryDate), 'MMMM d, yyyy')}
                  </p>
                  <span className={`text-sm font-medium ${MOODS.find(m => m.value === selectedEntry.mood)?.color}`}>
                    {MOODS.find(m => m.value === selectedEntry.mood)?.label}
                  </span>
                </div>
              </div>
            </div>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                {selectedEntry.content}
              </p>
            </div>
            <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button 
                onClick={() => handleEditEntry(selectedEntry)}
                variant="secondary"
                fullWidth
              >
                <Edit2 size={16} className="mr-2" />
                Edit
              </Button>
              <Button 
                onClick={() => handleDelete(selectedEntry.id)}
                className="bg-red-600 hover:bg-red-700"
                fullWidth
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Journal;
